'use client';

import { GlobalNavigationFrame } from './GlobalNavigationRail';

import { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { useExploreConsultationDraftStore } from '@/components/explore/exploreConsultationDraftStore';
import { requestExploreComposerOpen } from '@/components/explore/exploreComposerNavigation';
import { canSeedProductIdeaDraft, readProductIdea } from '@/lib/routing/product-idea-entry';
import { restoreExploreGenerationPreferences } from '@/components/explore/exploreGenerationSettingsStore';
import { AppSidebar, type SidebarNavItem } from '@/components/layout/AppSidebar';
import { useExploreImageComposerStore } from '@/components/explore/exploreImageComposerStore';
import { useExploreVideoComposerStore } from '@/components/explore/exploreVideoComposerStore';
import { isWorkspaceMode, type WorkspaceMode } from '@/lib/workspace-mode';
import { getExploreHref, getExploreProjectHref, type ExploreTopTab } from '@/components/explore/exploreNavigation';
import { KagemaneLibrary } from '@/components/explore/KagemaneLibrary';
import { OnboardingCreationGuide } from '@/components/onboarding/OnboardingCreationGuide';
import {
  consumePendingExploreDraft,
  consumePendingExploreDraftFiles,
  PENDING_EXPLORE_COMPOSER_RESUME_EVENT,
  type PendingExploreComposerResumeDetail,
} from '@/lib/auth/pending-explore-draft';
import type { OnboardingCreationModule } from '@/lib/api/users';
import { toast } from '@/lib/stores/toast-store';
import {
  clearOnboardingStartQueryMarker,
  ONBOARDING_START_MODULE_EVENT,
  writePendingOnboardingStartIntent,
} from '@/lib/onboarding';
import type { ConversationCapabilityKey } from '@/features/conversation-capabilities/contract';
import { useAuthStore } from '@/stores/authStore';
import { useCustomStylesStore } from '@/stores/customStylesStore';
import { prepareProjectAccess } from '@/lib/project-access';

const ExplorePanel = dynamic(
  () => import('@/components/panels/ExplorePanel').then((module) => module.ExplorePanel),
  {
    ssr: false,
    loading: () => (
      <div className="grid h-full grid-cols-2 gap-4 p-5 sm:grid-cols-3 lg:grid-cols-4" aria-busy="true">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="aspect-[4/3] rounded-xl bg-[#ECEFF2]" />
        ))}
      </div>
    ),
  },
);

function readTopTab(): ExploreTopTab {
  if (typeof window === 'undefined') return 'material';
  if (window.location.pathname === '/template-library') return 'template';
  const tab = new URLSearchParams(window.location.search).get('tab');
  if (tab === 'create') return 'create';
  if (tab === 'assets') return 'my-assets';
  return 'material';
}

function getNavigationHref(nav: SidebarNavItem, topTab: ExploreTopTab): string | null {
  if (nav === 'explore') return getExploreHref(topTab);
  if (nav === 'publishing') return '/publishing';
  if (nav === 'admin') return '/admin';
  if (nav === 'director-board') return '/director-board';
  if (nav === 'trash') return '/trash';
  return null;
}

interface ExploreWorkspaceRouteProps {
  inspirationLibraryOnly?: boolean;
  kagemaneOnly?: boolean;
  initialTopTab?: ExploreTopTab;
}

export function ExploreWorkspaceRoute({
  inspirationLibraryOnly = false,
  kagemaneOnly = false,
  initialTopTab = 'material',
}: ExploreWorkspaceRouteProps = {}) {
  const router = useRouter();
  const t = useTranslations('explore');
  const ideaT = useTranslations('seoGuides.productPhotographyIdeas.entry');
  const userId = useAuthStore((state) => state.user?.id);
  const pendingDraftResumedRef = useRef(false);
  const projectOpenRequestRef = useRef(0);
  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null);
  const [topTab, setTopTab] = useState<ExploreTopTab>(initialTopTab);

  // 首次 hydration 复用服务器的 URL 状态，随后同步客户端导航入口。
  useEffect(() => { setTopTab(readTopTab()); }, [initialTopTab]);

  const openProject = useCallback((projectId: string, requestedView?: WorkspaceMode) => {
    const requestId = ++projectOpenRequestRef.current;
    const href = getExploreProjectHref(projectId, requestedView);
    const currentUserId = useAuthStore.getState().user?.id;
    setOpeningProjectId(projectId);
    router.prefetch(href);
    void (async () => {
      try {
        if (currentUserId) await prepareProjectAccess(projectId, currentUserId);
      } catch {
        // The destination owns denied and retry states if this check fails.
      } finally {
        if (requestId === projectOpenRequestRef.current) {
          router.push(href);
        }
      }
    })();
  }, [router]);

  useEffect(() => () => { projectOpenRequestRef.current += 1; }, []);

  const handleTopTabChange = useCallback((nextTab: ExploreTopTab) => {
    projectOpenRequestRef.current += 1;
    setOpeningProjectId(null);
    setTopTab(nextTab);
    if (inspirationLibraryOnly && nextTab === 'material') {
      router.push('/explore', { scroll: false });
      return;
    }
    const href = getExploreHref(nextTab);
    if (window.location.pathname === '/explore' && href.startsWith('/explore')) {
      // These are local views of one workspace. A server navigation would fetch
      // another RSC tree and remount the same sidebar/composers for every click.
      if (window.location.pathname + window.location.search !== href) window.history.pushState(null, '', href);
      return;
    }
    router.push(href, { scroll: false });
  }, [inspirationLibraryOnly, router]);

  const handleNavChange = useCallback((nav: SidebarNavItem) => {
    const href = getNavigationHref(nav, topTab);
    if (href) {
      projectOpenRequestRef.current += 1;
      setOpeningProjectId(null);
      router.push(href);
    }
  }, [router, topTab]);

  const handleStartOnboardingModule = useCallback((
    module: OnboardingCreationModule,
    capability: ConversationCapabilityKey,
  ) => {
    setTopTab('material');
    writePendingOnboardingStartIntent(module, capability);
    clearOnboardingStartQueryMarker();
    window.dispatchEvent(new CustomEvent(ONBOARDING_START_MODULE_EVENT, {
      detail: { module, capability },
    }));
  }, []);

  useEffect(() => {
    const handleProjectSwitch = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string; view?: string }>).detail;
      if (detail?.projectId) openProject(detail.projectId, isWorkspaceMode(detail.view) ? detail.view : undefined);
    };
    const handleHistoryChange = () => setTopTab(readTopTab());
    window.addEventListener('switch-project', handleProjectSwitch);
    window.addEventListener('popstate', handleHistoryChange);
    return () => {
      window.removeEventListener('switch-project', handleProjectSwitch);
      window.removeEventListener('popstate', handleHistoryChange);
    };
  }, [openProject]);

  useEffect(() => {
    if (!userId || pendingDraftResumedRef.current) return;

    pendingDraftResumedRef.current = true;
    const pendingDraft = consumePendingExploreDraft();
    if (!pendingDraft) return;

    restoreExploreGenerationPreferences(pendingDraft.generationPreferences);
    if (pendingDraft.entry === 'consultation') {
      useExploreImageComposerStore.getState().openEmpty();
      useExploreImageComposerStore.getState().setDraft(pendingDraft.prompt);
      useExploreConsultationDraftStore.setState({ prompt: pendingDraft.prompt, files: [], restoring: true });
      void (async () => {
        try {
          const files = await consumePendingExploreDraftFiles(pendingDraft);
          useExploreConsultationDraftStore.setState({ files });
          if (files.length !== pendingDraft.attachments.length) toast.error(t('imageCreateUploadFailed'));
        } catch {
          toast.error(t('imageCreateUploadFailed'));
        } finally {
          useExploreConsultationDraftStore.setState({ restoring: false });
        }
      })();
      return;
    }

    if (pendingDraft.capability) {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent<PendingExploreComposerResumeDetail>(
          PENDING_EXPLORE_COMPOSER_RESUME_EVENT,
          { detail: { capability: pendingDraft.capability, generationPreferences: pendingDraft.generationPreferences } },
        ));
      });
    }

    if (pendingDraft.mode === 'video') {
      const store = useExploreVideoComposerStore.getState();
      store.openEmpty();
      store.setDraft(pendingDraft.prompt);
      if (pendingDraft.attachments.length > 0) {
        void (async () => {
          try {
            const files = await consumePendingExploreDraftFiles(pendingDraft);
            if (files.length !== pendingDraft.attachments.length) {
              toast.error(t('videoCreateUploadFailed'));
              return;
            }

            const uploadResult = await useExploreVideoComposerStore.getState().uploadReferenceImages(
              files,
              pendingDraft.attachments.map((attachment) => attachment.capabilitySlotKey),
            );
            if (!uploadResult.ok) toast.error(t('videoCreateUploadFailed'));
          } catch {
            toast.error(t('videoCreateUploadFailed'));
          }
        })();
      }
      return;
    }

    const store = useExploreImageComposerStore.getState();
    store.openEmpty();
    store.setDraft(pendingDraft.prompt);
    if (pendingDraft.style) {
      store.setSelectedCustomStyle({
        id: pendingDraft.style.id,
        preset_id: pendingDraft.style.presetId,
        source: 'system_preset',
        is_system: true,
        name: pendingDraft.style.name,
        name_en: pendingDraft.style.nameEn,
        name_zh: pendingDraft.style.nameZh,
        cover_url: pendingDraft.style.coverUrl,
        anchor_count: 1,
        style_reference_count: 1,
        analysis_status: 'completed',
      });
      if (pendingDraft.style.creativity !== undefined) {
        // saveCreativity exposes loading/error and blocks a premature Canvas send.
        // The selected template and its preview level survive password/OAuth sign-in.
        void useCustomStylesStore.getState().saveCreativity(
          pendingDraft.style.id, pendingDraft.style.creativity,
          { loadPreference: true, supersedeConversation: true },
        ).catch(() => toast.error(t('styleShowcaseResumeFailed')));
      }
    }

    if (pendingDraft.attachments.length > 0) {
      void (async () => {
        try {
          const files = await consumePendingExploreDraftFiles(pendingDraft);
          if (files.length !== pendingDraft.attachments.length) {
            toast.error(t('imageCreateUploadFailed'));
            return;
          }

          const uploadResult = await useExploreImageComposerStore.getState().uploadReferenceImages(
            files,
            pendingDraft.attachments.map((attachment) => attachment.capabilitySlotKey),
          );
          if (!uploadResult.ok) toast.error(t('imageCreateUploadFailed'));
        } catch {
          toast.error(t('imageCreateUploadFailed'));
        }
      })();
    }
  }, [t, userId]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const idea = readProductIdea(url.searchParams.get('product_idea'));
    if (!idea || inspirationLibraryOnly || kagemaneOnly) return;
    // Consume only the direction marker; attribution and locale have their own lifecycle.
    url.searchParams.delete('product_idea');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (!canSeedProductIdeaDraft(
      useExploreConsultationDraftStore.getState(),
      useExploreImageComposerStore.getState(),
      useExploreVideoComposerStore.getState(),
    )) {
      toast.info(ideaT('existingDraft'));
      return;
    }
    const prompt = ideaT(idea);
    useExploreImageComposerStore.getState().setDraft(prompt);
    useExploreConsultationDraftStore.setState({ prompt });
    setTopTab('material');
    requestExploreComposerOpen('image');
  }, [ideaT, inspirationLibraryOnly, kagemaneOnly]);

  const studioNavigation = !inspirationLibraryOnly && !kagemaneOnly;

  return (
    <GlobalNavigationFrame className="relative flex h-full flex-col overflow-hidden bg-surface-base">
      <AppSidebar
        toolsNavigation={studioNavigation ? { active: topTab === 'my-assets' ? 'assets' : 'explore' } : undefined}
        activeNav="explore"
        exploreTopTab={topTab}
        showProjectShortcut={false}
        onExploreTopTabChange={handleTopTabChange}
        onNavChange={handleNavChange}
        onProjectSelect={openProject}
      />
      <main className="relative min-h-0 min-w-0 flex-1 overflow-y-auto">
        {openingProjectId ? (
          <p role="status" aria-live="polite" className="absolute left-1/2 top-4 z-30 -translate-x-1/2 rounded-full border border-[#4ABDE9]/20 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm">
            {t('openingProject')}
          </p>
        ) : null}
        {kagemaneOnly ? (
          <KagemaneLibrary
            onBack={() => router.push('/explore')}
          />
        ) : (
          <ExplorePanel
            showProjectSidebar={!studioNavigation}
            topTab={topTab}
            onTopTabChange={handleTopTabChange}
            inspirationLibraryOnly={inspirationLibraryOnly}
          />
        )}
      </main>
      {!inspirationLibraryOnly && !kagemaneOnly ? (
        <OnboardingCreationGuide onStartModule={handleStartOnboardingModule} />
      ) : null}
    </GlobalNavigationFrame>
  );
}
